import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { usePersistedCacheQuery } from '../../apollo';
import {
    AllBudgetCategoryGroupsDocument,
    TransactionEntriesFilteredDocument,
} from '../../graphql/generated';
import { sortBy } from '../../util/arrays';
import { roundCentsToNearestDollar } from '../../util/currency';
import { dt } from '../../util/dateTime';
import { isNotNullish } from '../../util/types';
import {
    useFinancialEntityMap,
    useTransactionEntries,
    useTransactionEntryCategories,
} from '../cashflow';
import { useTransactionEntryCategoryGroups } from '../cashflow/useTransactionEntryCategoryGroups';
import { buildBudgetCategoryGroupMap } from './buildBudgetCategoryGroupMap';
import { sumCategories } from './sumCategories';
import { BudgetCategoryGroupWithAverage, CategoryWithAverage } from './types';

const MONTHS_TO_FETCH = 3;

// TODO: Consider doing some of this on the server - logic is pretty complex
export const useBudgetCategoryGroupsWithAverage = (): {
    budgetCategoryGroups: BudgetCategoryGroupWithAverage[];
    loading: boolean;
} => {
    const { loading, data } = useRequiredData();

    return useMemo(() => {
        if (loading || !data) {
            return { loading: true, budgetCategoryGroups: [] };
        }

        const {
            budgetCategoryGroups,
            transactionEntryCategoryGroups,
            transactionEntryCategoriesMapById,
            transactionEntries,
        } = data;

        const { categorySums, categoryGroupSums } = sumCategories({
            transactionEntries,
        });

        const budgetCategoryGroupMap =
            buildBudgetCategoryGroupMap(budgetCategoryGroups);

        const budgetCategoryGroupsWithAverage = transactionEntryCategoryGroups
            .map((group) => {
                const budgetCategoryGroup = budgetCategoryGroupMap[group.id];

                const budgetPurpose =
                    budgetCategoryGroup?.budgetPurpose ||
                    group.defaultBudgetPurpose;

                if (!budgetPurpose) return null;

                const categoryGroupSum = categoryGroupSums.find(
                    (categoryGroupSum) => categoryGroupSum.id === group.id
                );

                const categoryAverages: CategoryWithAverage[] =
                    group.transactionEntryCategoriesByCategoryGroupId
                        .map(({ id }) => {
                            const category =
                                transactionEntryCategoriesMapById[id];
                            if (!category) return;

                            const categorySum = categorySums.find(
                                (s) => s.category.id === category.id
                            );

                            let average = 0;

                            if (categorySum) {
                                const numMonthsWithSpending = Object.keys(
                                    categorySum.amountByMonth
                                ).length;

                                average = roundCentsToNearestDollar(
                                    -categorySum.amount / numMonthsWithSpending
                                );
                            }

                            return {
                                id: category.id,
                                average,
                                category,
                                transactionEntries: transactionEntries.filter(
                                    (t) => t.category.id === id
                                ),
                            };
                        })
                        .filter(isNotNullish);

                const amountByMonth = categoryGroupSum?.amountByMonth || {};

                // When calculating the average, ignore months that have $0 for spending
                const numMonthsWithSpending = Object.keys(amountByMonth).length;
                const average = categoryGroupSum
                    ? roundCentsToNearestDollar(
                          -categoryGroupSum.amount / numMonthsWithSpending
                      )
                    : 0;

                return {
                    id: budgetCategoryGroup?.id,
                    average,
                    amountByMonth,
                    // If a spending group happens to be negative due to refunds, set the budget to $0
                    amount: budgetCategoryGroup?.amount || Math.max(0, average),
                    categoryGroup: group,
                    enabled: budgetCategoryGroup
                        ? budgetCategoryGroup.enabled
                        : false,
                    budgetPurpose,
                    categoryAverages: sortBy(
                        categoryAverages,
                        (c) => c.category.name
                    ),
                };
            })
            .filter(isNotNullish);

        return {
            budgetCategoryGroups: sortBy(
                budgetCategoryGroupsWithAverage,
                (g) => g.categoryGroup.name,
                'ASC'
            ),
            loading: false,
        };
    }, [loading, data]);
};

const useRequiredData = () => {
    const lastMonth = dt().subtract(1, 'month').endOf('month');
    const startDate = lastMonth.subtract(MONTHS_TO_FETCH, 'months').toDateStr();
    const endDate = lastMonth.toDateStr();

    const {
        data: transactionEntriesQuery,
        loading: transactionEntriesLoading,
    } = usePersistedCacheQuery(TransactionEntriesFilteredDocument, {
        variables: {
            filter: {
                date: {
                    greaterThan: startDate,
                    lessThanOrEqualTo: endDate,
                },
                isHidden: { equalTo: false },
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const { financialEntityMap, financialEntityMapLoading } =
        useFinancialEntityMap();

    const {
        transactionEntryCategoriesMapById,
        transactionEntryCategoriesLoading,
    } = useTransactionEntryCategories();

    const {
        transactionEntryCategoryGroups,
        transactionEntryCategoryGroupsLoading,
    } = useTransactionEntryCategoryGroups();

    const transactionEntries = useTransactionEntries({
        transactionEntries: transactionEntriesQuery?.transactionEntries,
        financialEntityMap,
        transactionEntryCategoriesMapById,
    });

    const {
        data: budgetCategoryGroupsData,
        loading: budgetCategoryGroupsLoading,
    } = useQuery(AllBudgetCategoryGroupsDocument);

    if (
        budgetCategoryGroupsLoading ||
        transactionEntryCategoriesLoading ||
        transactionEntriesLoading ||
        financialEntityMapLoading ||
        transactionEntryCategoryGroupsLoading ||
        !budgetCategoryGroupsData?.budgetCategoryGroups ||
        !transactionEntryCategoriesMapById ||
        !transactionEntryCategoryGroups
    ) {
        return { loading: true, data: undefined };
    }

    return {
        loading: false,
        data: {
            transactionEntries,
            budgetCategoryGroups: budgetCategoryGroupsData.budgetCategoryGroups,
            transactionEntryCategoriesMapById,
            transactionEntryCategoryGroups,
        },
    };
};
